import React from 'react'
import awsRequirements from '../../mocks/awsRequirementsSection.json'
import { BoxV2, ButtonV2, ExternalLink } from '@labrav/react-components'
import { Typography } from '@mui/material'
import DOMPurify from 'dompurify'
import { useStyles } from './FlyOutOnboardingAWSRequirements.styles'
import clsx from 'clsx'
import { RequirementsAction } from '../../common/components/RequirementsAction/RequirementsAction'
import { CloudType } from '../../types/index'
import { FooterV2 } from '../../common/components/FooterV2/FooterV2'
import { useNavigate } from 'react-router-dom'
export interface FlyOutOnboardingAWSContent {
  type: string
  text: string
  link?: string
  linkText?: string
}
export interface FlyOutOnboardingAWSContentWithNextLines
  extends FlyOutOnboardingAWSContent {
  nextLines?: FlyOutOnboardingAWSContent[]
}
export interface FlyOutOnboardingAWSRequirements {
  title: string
  reqLink: string
  contents: FlyOutOnboardingAWSContentWithNextLines[]
}

export interface FlyOutOnboardingAWSRequirementsProps {
  isProductCreation?: boolean
}
export const FlyOutOnboardingAWSRequirements: React.FC<
  FlyOutOnboardingAWSRequirementsProps
> = ({ isProductCreation = false }) => {
  const requirements = (
    !isProductCreation ? awsRequirements?.slice(0, 1) : awsRequirements
  ) as FlyOutOnboardingAWSRequirements[]
  const { classes } = useStyles()
  const requirementName = 'AWS requirements'
  const navigate = useNavigate()
  const goToCompanyDetails = () => {
    navigate('/flyout/onboarding/company-information')
  }
  return (
    <div data-testid="requirements-page">
      {requirements.map(
        (requirement: FlyOutOnboardingAWSRequirements, index) => (
          <div
            key={index}
            className={
              index === requirements.length - 1
                ? !isProductCreation
                  ? classes.marginBottom
                  : ''
                : classes.requirementsMarginBottom
            }
          >
            <BoxV2
              heading={requirement.title}
              hideHeading={false}
              actions={
                <RequirementsAction
                  link={requirement.reqLink}
                  linkText={requirementName}
                  cloudType={CloudType.AWS}
                />
              }
            >
              <div
                className={classes.rootReqContainer}
                data-testid="content-block-req"
              >
                <ol
                  type="1"
                  className={clsx(classes.orderedList, classes.contentText)}
                >
                  {requirement.contents.map(
                    (content: FlyOutOnboardingAWSContentWithNextLines) => {
                      return (
                        <div
                          className={classes.textContainer}
                          data-testid="text-requirement"
                        >
                          <li>
                            <GetRequirementsContent content={content} />
                            {content?.nextLines &&
                              content?.nextLines.map(
                                (nextLine: FlyOutOnboardingAWSContent) => (
                                  <GetRequirementsContent content={nextLine} />
                                )
                              )}
                          </li>
                        </div>
                      )
                    }
                  )}
                </ol>
              </div>
            </BoxV2>
          </div>
        )
      )}
      {!isProductCreation && (
        <FooterV2
          actions={
            <div className={classes.footerWrapper}>
              <ButtonV2
                data-testid="continue-button"
                styleType="primary"
                onClick={goToCompanyDetails}
              >
                Continue
              </ButtonV2>
            </div>
          }
        />
      )}
    </div>
  )
}

export interface GetRequirementsContentProps {
  content: FlyOutOnboardingAWSContent
}
export const GetRequirementsContent = React.memo(
  ({ content }: GetRequirementsContentProps) => {
    const { classes } = useStyles()
    const sanitizer = DOMPurify.sanitize
    switch (content.type) {
      case 'text': {
        return (
          <div
            data-testid="answer-text"
            className={classes.contentText}
            dangerouslySetInnerHTML={{
              __html: sanitizer(content?.text || ''),
            }}
          />
        )
      }
      case 'textWithLink': {
        return (
          <Typography
            className={classes.contentText}
            data-testid="textWithLink"
          >
            {content?.text + ' '}
            <ExternalLink href={content?.link || ''}>
              {content.linkText}
            </ExternalLink>
          </Typography>
        )
      }
      default:
        return <></>
    }
  }
)
